// eBay Fee Rates — verified June 2026
// Sources: ebayfeescalculator.com/uk-ebay-calculator (Apr 2026)
//          ebayfeescalculator.com/usa-ebay-calculator (Apr 2026)
// UK: Business seller, most categories, Managed Payments
// US: No store subscriber, most categories, orders >$10
// DE: Most categories, Managed Payments
// AU: Standard rate (AU$25k+ annual sales sellers)
// Update this file when eBay changes rates — single source of truth.

import flagGB from '../../../../assets/images/flags/gb.svg';
import flagUS from '../../../../assets/images/flags/us.svg';
import flagDE from '../../../../assets/images/flags/de.svg';
import flagAU from '../../../../assets/images/flags/au.svg';

export interface MarketConfig {
  flag: string;
  flagSrc: string;
  code: string;
  label: string;
  currency: string;
  currencySymbol: string;
  fvfPercent: number;          // default (most categories)
  fixedFee: number;
  maxFeeCap: number;
  storeDiscount: number;
  categoryRates?: Record<string, number>;  // kategori bazlı override
  regulatoryFeePercent?: number;           // UK: 0.42
  vatOnFeesPercent?: number;               // UK business: 20
}

export const MARKETS: MarketConfig[] = [
  {
    flag: '🇬🇧',
    flagSrc: flagGB,
    code: 'UK',
    label: '🇬🇧 United Kingdom',
    currency: 'GBP',
    currencySymbol: '£',
    fvfPercent: 12.9,
    fixedFee: 0.30,
    maxFeeCap: 250,
    storeDiscount: 1.0,   // 12.9 → 11.9 with store
    categoryRates: {
      most:        12.9,
      electronics: 8.9,
      videogames:  8.9,
      clothing:    12.9,
      jewellery:   12.9,
      home:        12.9,
      toys:        12.9,
      health:      12.9,
      sports:      12.9,
    },
    // UK eBay fees are quoted VAT-inclusive.
    // regulatory fee is embedded in the FVF rate.
    // Do NOT add vatOnFeesPercent or regulatoryFeePercent here.
    // Verified against real order: £13.90 sale → £2.03 fees (June 2026)
  },
  {
    flag: '🇺🇸',
    flagSrc: flagUS,
    code: 'US',
    label: '🇺🇸 United States',
    currency: 'USD',
    currencySymbol: '$',
    fvfPercent: 13.25,
    categoryRates: {
      most:        13.25,
      electronics: 13.25,
      clothing:    13.25,
      home:        13.25,
      toys:        13.25,
      health:      13.25,
      sports:      13.25,
    },
    fixedFee: 0.40,
    maxFeeCap: 750,
    storeDiscount: 0.9,   // 13.25 → 12.35 with store
  },
  {
    flag: '🇩🇪',
    flagSrc: flagDE,
    code: 'DE',
    label: '🇩🇪 Germany',
    currency: 'EUR',
    currencySymbol: '€',
    fvfPercent: 11.9,
    categoryRates: {
      most:        9.8,
      electronics: 5.0,
      clothing:    9.8,
      home:        9.8,
      toys:        9.8,
      health:      9.8,
      sports:      9.8,
    },
    vatOnFeesPercent: 19,
    fixedFee: 0.35,
    maxFeeCap: 200,
    storeDiscount: 1.0,
  },
  {
    flag: '🇦🇺',
    flagSrc: flagAU,
    code: 'AU',
    label: '🇦🇺 Australia',
    currency: 'AUD',
    currencySymbol: 'A$',
    fvfPercent: 13.4,
    fixedFee: 0.30,
    maxFeeCap: 400,
    storeDiscount: 1.0,
  },
];

export interface FeeCalcOpts {
  category?: string;
  sellerType?: 'private' | 'business';
  isVatRegistered?: boolean;
  isTopRatedPlus?: boolean;
  hasStore?: boolean;
}

export function calculateFees(
  salePrice: number,
  market: MarketConfig,
  opts: FeeCalcOpts = {}
): {
  fvfPercent: number;
  fvfAmount: number;
  fixedFee: number;
  regulatoryFee: number;
  vatAmount: number;
  totalFee: number;
  netProfit: number;
} {
  const {
    category = 'most',
    sellerType = 'business',
    isVatRegistered = false,
    isTopRatedPlus = false,
    hasStore = false,
  } = opts;

  // Private seller: no FVF since Oct 2024
  if (sellerType === 'private') {
    return {
      fvfPercent: 0,
      fvfAmount: 0,
      fixedFee: market.fixedFee,
      regulatoryFee: 0,
      vatAmount: 0,
      totalFee: market.fixedFee,
      netProfit: parseFloat((salePrice - market.fixedFee).toFixed(2)),
    };
  }

  // Step 1: base FVF rate (category override or default)
  const baseRate =
    market.categoryRates && market.categoryRates[category] !== undefined
      ? market.categoryRates[category]
      : market.fvfPercent;

  // Step 2: store discount
  const rateAfterStore = hasStore
    ? baseRate - market.storeDiscount
    : baseRate;

  // Step 3: Top Rated Plus — 10% off FVF rate (applied before VAT)
  const rateAfterTopRated = isTopRatedPlus
    ? rateAfterStore * 0.9
    : rateAfterStore;

  // Step 4: FVF amount (with cap)
  const fvfAmountRaw = Math.min(
    (salePrice * rateAfterTopRated) / 100,
    market.maxFeeCap
  );

  // Step 5: regulatory fee (UK 0.42%)
  const regulatoryFee =
    market.regulatoryFeePercent
      ? parseFloat((salePrice * market.regulatoryFeePercent / 100).toFixed(2))
      : 0;

  // Step 6: subtotal before VAT
  const subtotalBeforeVat = fvfAmountRaw + market.fixedFee + regulatoryFee;

  // Step 7: VAT on fees (UK VAT-registered business)
  const vatAmount =
    market.vatOnFeesPercent && isVatRegistered
      ? parseFloat((subtotalBeforeVat * market.vatOnFeesPercent / 100).toFixed(2))
      : 0;

  // Step 8: totals
  const totalFee = parseFloat(
    (subtotalBeforeVat + vatAmount).toFixed(2)
  );
  const fvfAmount = parseFloat(fvfAmountRaw.toFixed(2));
  const netProfit = parseFloat((salePrice - totalFee).toFixed(2));

  return {
    fvfPercent: parseFloat(rateAfterTopRated.toFixed(2)),
    fvfAmount,
    fixedFee: market.fixedFee,
    regulatoryFee,
    vatAmount,
    totalFee,
    netProfit,
  };
}
